import { Document } from '@contentful/rich-text-types'
import { AiDetectorResponse, LensedAlert } from '@grammarly/http-clients-capi/lib/capi-http'
import { LogErrorMetrics } from '@lib/tracking/felog'

import * as CmsMetrics from '@lib/tracking/metrics/metrics'
import { TypeSingleColumn } from '@lib/types'
import { transformToString } from '@lib/utils/error'
import { UrlBasedUsageTracker } from '@lib/utils/tool-usage'
import { DEFAULT_TOOL_API_ERROR_MESSAGE } from './constants'

// Not including citationGenerator, wordCounter, etc. for now
export type ToolType =
  | 'aiDetector'
  | 'grammarChecker'
  | 'plagiarismChecker'
  | 'paraphraser'
  | 'humanizer'
  | 'goTemplatesGeneric'

export type GoTemplatesToolType = Extract<ToolType, 'paraphraser' | 'humanizer' | 'goTemplatesGeneric'>

export const isGoTemplatesToolType = (toolType: ToolType): toolType is GoTemplatesToolType =>
  toolType === 'paraphraser' || toolType === 'humanizer' || toolType === 'goTemplatesGeneric'

export type ToolApi = (formData: FormData) => Promise<void>

export const toolTypeToMetricToolType: Record<ToolType, CmsMetrics.ToolType> = {
  aiDetector: CmsMetrics.ToolType.AiDetector,
  grammarChecker: CmsMetrics.ToolType.GrammarCheck,
  plagiarismChecker: CmsMetrics.ToolType.PlagiarismChecker,
  paraphraser: CmsMetrics.ToolType.GoTemplates,
  humanizer: CmsMetrics.ToolType.GoTemplates,
  goTemplatesGeneric: CmsMetrics.ToolType.GoTemplates,
} as const

export const toolTypeToLogErrorMetric: Record<ToolType, LogErrorMetrics> = {
  aiDetector: LogErrorMetrics.AIDetectorError,
  grammarChecker: LogErrorMetrics.GrammarCheckError,
  plagiarismChecker: LogErrorMetrics.PlagiarismCheckerError,
  paraphraser: LogErrorMetrics.GOToolError,
  humanizer: LogErrorMetrics.GOToolError,
  goTemplatesGeneric: LogErrorMetrics.GOToolError,
} as const

export interface ToolFormValues {
  [key: string]: string
}

export interface ToolProps {
  toolType: ToolType

  inputName?: string
  placeholder?: string

  // Toolbar
  submitButtonText?: string
  canUploadFile?: boolean

  // Word Limits
  maxWordLimit?: number
  minWordLimit?: number

  // Output
  outputTitle: string

  // Usage Limits
  anonymousUsageLimit?: number
  freeUsageLimit?: number
  proUsageLimit?: number

  // Footer Banner
  footerBannerCopy?: string

  // Go Templates
  llmPrompt?: Document
  signupWall?: TypeSingleColumn
  upgradeWall?: TypeSingleColumn
  sampleText?: string
}

export interface ToolOutputContentProps {
  isLoading: boolean
  outputContent: React.ReactNode
}

export type ToolTypeToMockToolResponse = {
  ['plagiarismChecker']: LensedAlert[]
  ['aiDetector']: AiDetectorResponse
}

//https://github.com/microsoft/TypeScript/wiki/FAQ#why-doesnt-extending-built-ins-like-error-array-and-map-work

export class ToolApiError extends Error {
  constructor(m: string) {
    super(m)
    this.name = 'ToolApiError'
    Object.setPrototypeOf(this, ToolApiError.prototype)
  }
}

export const toolErrorToString = (error: unknown) => {
  if (error instanceof Error) {
    if (error instanceof ToolApiError) {
      return DEFAULT_TOOL_API_ERROR_MESSAGE
    }
    return error.message
  } else {
    return transformToString(error as object)
  }
}

export interface WallGuardConfig {
  shouldTriggerTextLimitWall: boolean
  triggerTextLimitWall: () => void
  shouldTriggerGoTemplatesWall: boolean
  triggerUsageWall: () => void
  toolUsageTracker?: UrlBasedUsageTracker
}

interface WallGuardNoWallResult {
  shouldWall: false
}
export interface WallGuardWallResult {
  shouldWall: true
  wallToTrigger: 'textLimit' | 'goTemplates' | 'usage'
  wallTrigger: () => void
}

export type WallGuardResult = WallGuardNoWallResult | WallGuardWallResult

export interface ToolOnSubmitParams {
  toolType: ToolType
  toolApi: ToolApi
  wallGuard: () => WallGuardResult
  setIsLoading: (isLoading: boolean) => void
  setToolError: (toolError: string | null) => void
  pageId: string
  slug: string
  toolUsageTracker?: UrlBasedUsageTracker
  sampleText?: string
  toolInputName: string
  setShowToolsGoogleOneTap: (show: boolean) => void
  isGoogleOneTapOnTaskComplete: () => boolean
}
